"use client";
import { useContext, useEffect, useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    CircularProgress,
} from "@mui/material";
import { FiPaperclip } from "react-icons/fi";
import { clientDetails } from "../../services/Client/ClientApi";
import Utils from "../../middleware/Utils";
import { AuthContext } from "../../Context/ContextFunctions";

export default function TenderPreviewDialog({ open, onClose, partiesKeyID, modelRequestData }) {
    const { formatDateDMY } = useContext(AuthContext)
    const [loading, setLoading] = useState(false);
    const [details, setDetails] = useState(null);

    useEffect(() => {

        if (!open || !partiesKeyID) return;

        const fetchData = async () => {
            setLoading(true);
            try {
                const res = await clientDetails(partiesKeyID);
                setDetails(res.responseData.data || []);
            } catch (error) {
                console.log("error ==>>", error)

            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [open, partiesKeyID]);

    const Field = ({ label, value, attachment }) => (
        <div>
            <p className="text-[14px] text-black-600 font-semibold flex items-center gap-1">
                {label}
                {attachment && (
                    <a href={attachment} target="_blank" rel="noopener noreferrer">
                        <FiPaperclip className="text-blue-600 cursor-pointer" />
                    </a>
                )}
            </p>
            <p className="text-[12px] font-medium text-gray-900 mt-0.5 break-all">
                {value || "—"}
            </p>
        </div>
    );

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
            {/* Header */}
            <div className="bg-[#ECF6FF] px-6 py-4 border-b flex justify-between items-center">
                <h2 className="text-16 font-semibold"> Tender Details </h2>
                <button
                    className="text-gray-500 hover:text-gray-700"
                    onClick={onClose}
                >
                    ✕
                </button>
            </div>


            {/* -------------------- MAIN CONTENT (SCROLLABLE) -------------------- */}
            <DialogContent
                className="p-6 bg-gray-50 space-y-10 max-h-[75vh] overflow-y-auto"
            >
                {loading && (
                    <div className="flex justify-center py-20">
                        <CircularProgress />
                    </div>
                )}

                {!loading && !details && (
                    <p className="text-center text-gray-500 py-10">
                        No tender details found.
                    </p>
                )}

                {!loading && details && (
                    <>
                        {/* -------------------- SECTIONS GROUP -------------------- */}
                        {[
                            {
                                title: "Tender Information",
                                items: [
                                    ["Tender Id/Quotation No.", details.tenderID],
                                    ["Tender Date", formatDateDMY(details.tenderDate)],
                                    ["Tender Type", details.tenderType],
                                    ["Processing Fee", "₹ " + details.processingFee, details.processingFeeAttachment],
                                    ["Form Fee", "₹ " + details.formFee, details.formFeeAttachment],
                                    // ["EMD", details.emd],
                                    ["EMD Payment", details.eemPayment, details.emdAttachment],//spelling mistake from backend
                                    ["EMD Payment Source", Utils.paymentSource?.find(item => item.value == details.emdPaymentSource)?.label || "—"],
                                    ["Security Deposit", "₹ " + details.securityDeposit, details.securityDepositAttachment],
                                    ["Advance Security Deposit", "₹ " + details.additionalSecurityDeposit, details.additionalSecurityDepositAttachment],
                                    ["Performance Security Deposit", "₹ " + details.performanceSecurityDeposit ? details.performanceSecurityDeposit : 0, details.performanceSecurityDepositAttachment],
                                    ["Bank Guarantee", details?.bankGuarantee == "False" ? "No" : "Yes"],
                                    ["Bank Start Date", formatDateDMY(details?.startDate)],
                                    ["Bank End Date", formatDateDMY(details?.endDate)],
                                    ["Agreement Stamp Duty", "₹ " + details.agreementStampDuty, details.agreementAttachment],
                                    ["TDS", "₹ " + details?.tds],
                                    ["GST Amount", "₹ " + details?.gstAmount],
                                    ["Insurance Fee", "₹ " + details?.insuranceFee],
                                ],
                            },
                            {
                                title: "Client Information",
                                items: [
                                    ["Company Name", details.companyName],
                                    ["Company Address", details.companyAddress],
                                    ["Pincode", details.pincode],
                                    ["Email", details.email],
                                    ["Website", details.website],
                                    ["GST Registered", details.gstRegistered == "False" ? "No" : "Yes"],
                                    ["GST Number", details.gstNumber, details.gstAttachment],

                                ],
                            },
                            {
                                title: "Project Information",
                                items: [
                                    ["Client Name", details.clientName],
                                    ["Location", details.location],
                                    ["Department", details.department],
                                    ["Work Order", details.scopeOfWork, details.workOrder],
                                    ["Project Duration", details.projectDurationDays ? `${details.projectDurationDays} Days` : "—"],
                                    ["Cost to Client", "₹ " + details.costToClient ? details.costToClient : 0],
                                    ["Cost to Technoland", "₹ " + details.costToTechnoland ? details.costToTechnoland : 0],
                                ],
                            },
                        ].map((section, index) => (
                            <div
                                key={index}
                                className="bg-white rounded-xl shadow p-6 border border-gray-200"
                            >
                                {/* Section Title */}
                                <h3 className="text-lg font-semibold text-[#0F2944] mb-4">
                                    {section.title}
                                </h3>
                                <div className="border-b border-gray-200 mb-5"></div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                                    {Array.isArray(section.items) && section.items.map(([label, value, attachment], idx) => (
                                        <Field key={idx} label={label} value={value} attachment={attachment} />
                                    ))}
                                </div>
                            </div>
                        ))}

                        {/* -------------------- Contact Persons -------------------- */}
                        {/* <div className="bg-white rounded-xl shadow p-6 border border-gray-200">
                            <h3 className="text-lg font-semibold text-[#0F2944] mb-4">
                                Contact Information
                            </h3>
                            <div className="border-b border-gray-200 mb-5"></div>
                            {details.contactPersons?.length > 0 ? (
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                    {details.contactPersons.map((cp) => (
                                        <div
                                            key={cp.contactID}
                                            className="bg-gray-50 p-4 rounded-lg border shadow-sm"
                                        >
                                            <div className="grid grid-cols-2 gap-4">
                                                <Field
                                                    label="Name"
                                                    value={`${cp.firstName} ${cp.lastName}`}
                                                />
                                                <Field label="Designation" value={cp.designation} />
                                                <Field label="Phone" value={cp.phone} />
                                                <Field label="Email" value={cp.email} />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-gray-500">No contacts available.</p>
                            )}
                        </div> */}
                    </>
                )}
            </DialogContent>

            {/* -------------------- STICKY FOOTER -------------------- */}
            <div className="sticky bottom-0 bg-white border-t z-20">
                <div className="flex justify-end p-4">
                    <button
                        onClick={onClose}
                        className="mx-2 px-4 py-2 bg-gray-300 rounded-md text-12"
                    >
                        Close
                    </button>
                </div>
            </div>
        </Dialog>
    );
}

